import * as R from 'ramda';
import * as React from 'react';
import { debounce } from 'throttle-debounce';

import { getLocalized } from '@utils/configUtils.ts';
import { getLocation } from '@utils/location.ts';
import { sortPois, promoteExactNameMatches, adjustBoundsToPois } from '@utils/poi.ts';
import { StateObjMultiple } from '@utils/stateObj.ts';

import { widgetEnum } from '../../../../utils/constants.ts';
import type { PluginConfig } from '../config-schema.ts';
import {
  SEARCH_VIEW_BODY_ID,
  SEARCH_VIEW_CATEGORY_LIST_ID,
  SEARCH_VIEW_TOGGLER,
  MAX_UI_RESULTS_LENGTH,
  ToggleViewState,
} from '../constants.ts';
import CategoryListView from './CategoryListView.tsx';
import SearchResultView from './SearchResultView.tsx';
import SearchViewToggler from './SearchViewToggler.tsx';

// Announce after search debounce AND screen reader search box content
const ANNOUNCE_DEBOUNCE_TIME = 1000;

function create(app, config: PluginConfig) {
  const hiddenCategories = new Set(); // if you wish to hide a category, add it to this list
  let lastHasLocation = false;

  const isLayerShowing = async id => app.bus.send('layers/isShowing', { id }).then(res => res.length > 0 && res[0]); // layerManager is not loaded for SDK

  function setPOICategoryState() {
    // First get the list of configuration-defined locale-sensitive POI Categories
    let poiCategories = getLocalized(config, 'poiCategories', app.i18n().language);

    // Next, remove any categories that have been requested to be hidden
    poiCategories = poiCategories.filter(({ category }) => !hiddenCategories.has(category));

    // Next, if we have the user's location and they are on a mobile device, add "nearby" catogory
    if (lastHasLocation) {
      const nearbyCategory = {
        name: app.gt()('homeView:Nearby'),
        searchTerm: app.gt()('homeView:Nearby'),
        category: 'nearby',
      };
      poiCategories = [nearbyCategory, ...poiCategories];
    }

    searchResultsWidgetState.update({ poiCategories });
  }

  const init = () => {
    app.bus.send('search/registerSpecialQuery', {
      term: app.gt()('homeView:Nearby'),
      event: 'search/queryNearby',
      params: {},
    });

    setPOICategoryState();
    app.bus.on('i18n/langChanged', setPOICategoryState); // refresh categories when language changes

    // This is bad - we shouldn't depend or know about poiView. But we already commit this crime.
    // A refactor which is more declaritive would be an improvement and could eliminate the need
    // to be aware of the poiView.
    if (app.config?.plugins['online/poiView']?.deepLinkProps?.poiId) {
      searchResultsWidgetState.update({ poiView: true });
    }
  };
  const searchResultsDefaultWidgetState = {
    keywords: [],
    pois: [],
    nearby: [],
    locations: [],
    isSearchConfirmed: false,
    toggleState: ToggleViewState.SWITCH_TO_MAP,
    lastTypeaheadTerm: '',
  };
  const searchResultsWidgetState = new StateObjMultiple({
    ...searchResultsDefaultWidgetState,
  });
  let searchRequestId = 0;
  let pendingConfirmedSearch;
  searchResultsWidgetState.addCallback(state =>
    app.bus.send('headerOnline/updateSearchConfirmed', {
      isSearchConfirmed: state.isSearchConfirmed,
    }),
  );

  async function performConfirmedQuery(term, searchMethod, referrer) {
    const confirmedSearch = { id: ++searchRequestId, term };
    pendingConfirmedSearch = confirmedSearch;
    const clearPendingConfirmedSearch = () => {
      if (pendingConfirmedSearch === confirmedSearch) {
        pendingConfirmedSearch = null;
      }
    };
    searchResultsWidgetState.update({
      pois: [],
      locations: [],
      nearby: [],
      isSearchConfirmed: true,
    });

    if (term) {
      // exclude empty term
      if (term.toLowerCase() === app.gt()('homeView:Nearby').toLowerCase()) {
        const pois = await Promise.resolve(app.bus.get('search/queryNearby')).finally(clearPendingConfirmedSearch);
        app.bus.send('event/search', {
          referrer,
          searchMethod,
          query: term,
          entities: pois.map(poi => poi.poiId),
        });
        return pois;
      } else {
        const result = await Promise.resolve(app.bus.send('search/queryWithSpecial', { term })).finally(
          clearPendingConfirmedSearch,
        );
        if (result.length === 1) {
          const pois = result[0]; // no gaurentee this is pois since special events can be anything.. but often is..
          // if its an array, assume an empty array is an empty list of POIs.. else ensure the items are POIs
          if (Array.isArray(pois) && (pois.length === 0 || (pois[0] && pois[0].poiId))) {
            app.bus.send('event/search', {
              referrer,
              searchMethod,
              query: term,
              entities: pois.map(poi => poi.poiId),
            });
          }
        }
      }
    } else {
      clearPendingConfirmedSearch();
    }
  }

  app.bus.send('ariaAnnouncer/registerNamespace', { announcementKey: 'homeViewSearchResults' });

  // Announce nearby and actual locations
  // When isNoResults is true announce exact content of "no-results" message in UI
  const sendResultsCountAnnouncement = (nearbyCount, locationsCount, { isNoResults = false } = {}) => {
    const T = app.gt();

    if (isNoResults) {
      app.bus.send('ariaAnnouncer/announce', {
        announcementKey: 'homeViewSearchResults',
        announcementText: `${T('homeView:Sorry, no matches were found')} ${T('homeView:Try searching a different phrase')}`,
      });
      return;
    }
    // Dedicated text for context that differs from UI
    const sections = [
      { text: T('homeView:_count_ matching places nearby', { count: nearbyCount }), count: nearbyCount },
      { text: T('homeView:_count_ other matching places', { count: locationsCount }), count: locationsCount },
    ]
      .filter(({ count }) => count > 0)
      .map(({ text }) => text);

    if (!sections.length) {
      return;
    }

    app.bus.send('ariaAnnouncer/announce', {
      announcementKey: 'homeViewSearchResults',
      announcementText: sections.join(', '),
    });
  };

  // During typeahead, announce only after the user pauses typing
  const announceResultsCountDebounced = debounce(ANNOUNCE_DEBOUNCE_TIME, sendResultsCountAnnouncement);

  app.bus.on('search/showSearchResults', ({ results, term }) => showSearchResults(results, term));
  app.bus.on('search/showNearby', ({ pois }) => showSearchResults(pois, app.gt()('homeView:Nearby'), false));

  function showSearchResults(pois, term, shouldOrder = true) {
    if (
      pendingConfirmedSearch &&
      (pendingConfirmedSearch.id !== searchRequestId || pendingConfirmedSearch.term !== term)
    ) {
      pendingConfirmedSearch = null;
      return;
    }
    pendingConfirmedSearch = null;

    searchResultsWidgetState.update({
      pois,
      nearby: [],
      locations: pois,
      keywords: [],
      isSearchConfirmed: true,
      toggleState: ToggleViewState.SWITCH_TO_MAP,
      isFilterChecked: false,
    });

    if (shouldOrder) {
      orderPoisIfDisplaying();
    } else {
      updateOpenStatuses(pois);
    }

    if (!searchResultsWidgetState.getState().poiView) {
      showResultsWidget();
      adjustBoundsToPois(pois, app);
    } else {
      searchResultsWidgetState.update({ poiView: false });
    }

    app.bus.send('searchResults/showPOIs', { pois });
    app.bus.send('map/selectEntities', {
      ids: Object.values(pois).map(poi => poi.poiId),
    });
    app.bus.send('headerOnline/updateTerm', { term, isSearchConfirmed: true });
  }

  /* app.bus.on('map/structureChanged', async ({ structure, userInitiated }) => {
    if (userInitiated && structure) {
      // const isCategoryListVisible = await isLayerShowing(SEARCH_VIEW_CATEGORY_LIST_ID)
      // if (isCategoryListVisible)
      app.bus.send('map/changeFloor', { id: structure.defaultLevelId })
    }
  }) */

  const orderPoisIfDisplaying = async () => {
    if (await areSearchResultsVisible()) {
      orderPois(searchResultsWidgetState.getState().pois);
    }
  };

  app.bus.on('map/moveEnd', orderPoisIfDisplaying);

  async function areSearchResultsVisible() {
    const isSearchBodyVisible = await isLayerShowing(SEARCH_VIEW_BODY_ID);
    const isSearchTogglerVisible = await isLayerShowing(SEARCH_VIEW_TOGGLER);
    return isSearchBodyVisible || isSearchTogglerVisible;
  }

  async function orderPois(pois) {
    if (!pois.length) {
      searchResultsWidgetState.update({ nearby: [], locations: [] });
      return;
    }

    const location = await getLocation(app.bus);
    if (!location) {
      searchResultsWidgetState.update({ nearby: [], locations: pois });
      return;
    }
    const { structureId } = location;
    const { nearby, locations } = await splitNearbyAndLocations(pois, structureId);

    const sortedNearby = promoteExactNameMatches(await sortPois(app.bus, nearby)).slice(0, MAX_UI_RESULTS_LENGTH);
    const sortedLocations = promoteExactNameMatches(await sortPois(app.bus, locations)).slice(0, MAX_UI_RESULTS_LENGTH);
    searchResultsWidgetState.update({
      nearby: sortedNearby,
      locations: sortedLocations,
    });
    updateOpenStatuses([...sortedNearby, ...sortedLocations]);
    sendResultsCountAnnouncement(sortedNearby.length, sortedLocations.length);
  }

  /**
   * Resolves open/closed status for the POIs currently on screen and publishes them as a
   * poiId -> status map on widget state.
   *
   * Status is deliberately resolved here rather than stored on the POI object, because it
   * depends on the current time. The map is view state: it is rebuilt whenever the results
   * change and discarded with them.
   */
  async function updateOpenStatuses(pois) {
    const list = Array.isArray(pois) ? pois : Object.values(pois ?? {});
    if (!list.length) {
      searchResultsWidgetState.update({ openStatuses: {} });
      return;
    }

    // getFirst resolves to undefined when poiDataManager is not loaded, while still letting a
    // genuine rejection from the listener surface rather than swallowing it.
    const entries = await Promise.all(
      list.map(async poi => [poi.poiId, await app.bus.getFirst('poi/getOpenStatus', { id: poi.poiId })]),
    );

    const openStatuses = Object.fromEntries(entries.filter(([, status]) => status !== undefined));
    searchResultsWidgetState.update({ openStatuses });
  }

  async function splitNearbyAndLocations(pois, structureId) {
    const isInCurrentBuilding = R.pathEq(structureId, ['position', 'buildingId']);
    const [inBuilding, otherBuildings] = R.partition(isInCurrentBuilding, pois);
    // If the 100% match would be the only nearby result, skip the nearby section
    // and let it surface at the top of locations instead.
    const hasGenuineNearby = inBuilding.some(poi => !poi.isExactNameMatch);
    if (!hasGenuineNearby) {
      return { nearby: [], locations: pois };
    }
    // Keep exact matches in nearby (sortPois floats them to the top) so the 100%
    // match shows wherever the map is panned, without duplicating into locations.
    const [exactFromOtherBuildings, locations] = R.partition(poi => poi.isExactNameMatch, otherBuildings);
    return { nearby: inBuilding.concat(exactFromOtherBuildings), locations };
  }
  const onEscapeKey = () => {
    app.bus.send('layers/show', { id: SEARCH_VIEW_CATEGORY_LIST_ID });
  };
  app.bus.send('layers/register', {
    id: SEARCH_VIEW_BODY_ID,
    widget: () => (
      <SearchResultView
        app={app}
        isDesktopFn={app.env.isDesktop}
        widgetState={searchResultsWidgetState}
        handleSuggestionClicked={term => performConfirmedQuery(term, 'keyword', 'searchInput')}
        handlePoiClicked={handlePoiClicked}
        handleEscapeKey={onEscapeKey}
        T={app.gt()}
      />
    ),
    layoutId: 'content',
  });

  function handlePoiClicked(poi) {
    app.bus.send('poiDetails/showPoi', {
      poi,
      info: { referrer: 'searchResultsList' },
    });
    if (app.env.isMobile()) {
      // Always pin toast to the very top; previously using a negative vh caused clipping on shorter viewports.
      // Toast plugin accepts arbitrary props (spread) including positioning values consumed by ToastComponent.
      app.bus.send('toast/show', {
        message: app.gt()(`homeView:Map displaying _building_/_level_`, {
          building: poi.position.structureName,
          level: poi.position.floorName,
        }),
        iconName: 'info',
        variant: 'info',
        top: 'calc(env(safe-area-inset-top, 0px) + 10px)',
        position: 'fixed',
      });
    }
  }

  app.bus.send('layers/register', {
    id: SEARCH_VIEW_CATEGORY_LIST_ID,
    widget: () => (
      <CategoryListView
        data-testid="CategoryListView"
        widgetState={searchResultsWidgetState}
        isMobile={app.env.isMobile()}
        onPoiCategoryButtonClick={onPoiCategoryButtonClick}
        T={app.gt()}
      />
    ),
    layoutId: 'content',
  });

  const onPoiCategoryButtonClick = poiObject => {
    if (poiObject.category === 'nearby') {
      performConfirmedQuery(app.gt()('homeView:Nearby'), 'customAction', 'customAction');
    } else {
      app.bus.send('search/customAction', poiObject);
    }
    performConfirmedQuery(poiObject.searchTerm || poiObject.category, 'keyword', 'searchInput');
  };

  // todo refactor displaying, hard to track if it's shown
  app.bus.send('layers/register', {
    id: SEARCH_VIEW_TOGGLER,
    widget: () => <SearchViewToggler onClickHandler={toggleViewHandler} widgetState={searchResultsWidgetState} />,
    layoutId: 'bottomBar',
    layoutName: 'fullscreen',
    shouldShow: currentlyShowing => {
      // array of widget IDs currently showing
      const { isSearchConfirmed, pois } = searchResultsWidgetState.getState();
      return isSearchConfirmed && pois.length !== 0 && !currentlyShowing.includes('poiViewMobile');
    },
    widgetType: widgetEnum.Mobile,
  });

  const toggleViewHandler = () => {
    const { toggleState } = searchResultsWidgetState.getState();
    if (toggleState === ToggleViewState.SWITCH_TO_MAP) {
      app.bus.send('layers/hide', { id: SEARCH_VIEW_BODY_ID });
      app.bus.send('layers/setPreviousLayer', SEARCH_VIEW_BODY_ID);
      searchResultsWidgetState.update({
        toggleState: ToggleViewState.SWITCH_TO_LIST,
      });
    } else {
      app.bus.send('layers/show', { id: SEARCH_VIEW_BODY_ID });
      app.bus.send('layers/setPreviousLayer', '');
      searchResultsWidgetState.update({
        toggleState: ToggleViewState.SWITCH_TO_MAP,
      });
    }
  };

  app.bus.on('homeview/performSearch', async ({ term, limit, isSearchConfirmed = false, searchMethod, referrer }) => {
    await app.bus.send('system/readywhenyouare');
    performSearchTypeahead(term, limit, isSearchConfirmed, searchMethod, referrer);
  });

  async function performSearchTypeahead(term, limit, isSearchConfirmed, searchMethod, referrer) {
    if (isSearchConfirmed) {
      return performConfirmedQuery(term, 'keyword', 'searchInput');
    }

    const currentSearchRequestId = ++searchRequestId;
    const [searchResult, location] = await Promise.all([
      app.bus.get('search/typeahead', { term, limit }),
      getLocation(app.bus),
    ]);

    const { keywords, pois } = searchResult;
    let nearby = [];
    let locations = pois.slice(0, MAX_UI_RESULTS_LENGTH);

    // Calculate split immediately if location is available
    if (location && pois.length > 0) {
      const { structureId } = location;
      const split = await splitNearbyAndLocations(pois, structureId);

      const sortedNearby = promoteExactNameMatches(await sortPois(app.bus, split.nearby)).slice(
        0,
        MAX_UI_RESULTS_LENGTH,
      );
      const sortedLocations = promoteExactNameMatches(await sortPois(app.bus, split.locations)).slice(
        0,
        MAX_UI_RESULTS_LENGTH,
      );

      nearby = sortedNearby;
      locations = sortedLocations;
    }

    if (currentSearchRequestId !== searchRequestId) {
      return;
    }

    searchResultsWidgetState.update({
      keywords,
      pois,
      locations,
      nearby,
      isSearchConfirmed,
      toggleViewState: ToggleViewState.SWITCH_TO_MAP,
      searchMethod,
      referrer,
      lastTypeaheadTerm: term,
    });

    const isNoResults = Boolean(term) && pois.length === 0;
    announceResultsCountDebounced(nearby.length, locations.length, { isNoResults });

    if (term) {
      showResultsWidget();
    } else {
      showCategoryList();
    }

    app.bus.send('searchResults/showPOIs', { pois });
    app.bus.send('map/selectEntities', {
      ids: Object.values(pois).map(poi => poi.poiId),
    });
    if (term) {
      app.bus.send('event/search', {
        referrer,
        searchMethod,
        query: term,
        entities: pois.map(poi => poi.poiId),
      });
    }
  }

  app.bus.on('search/showRecentSearches', async ({ recentSearches }) => {
    const searchSuggestions = await app.bus.get('search/getDefaultSearchTerms', { limit: 10 });
    const keywords = R.uniqBy(R.toLower, [].concat(recentSearches, searchSuggestions));
    searchResultsWidgetState.update({
      ...searchResultsDefaultWidgetState,
      keywords,
    });
    showSearchBody();
  });

  app.bus.on('searchResultsView/show', () => {
    searchResultsWidgetState.update({ ...searchResultsDefaultWidgetState });
    showCategoryList();
  });

  // todo refactor
  app.bus.on('searchResults/searchForTag', ({ tag }) => performConfirmedQuery(tag, 'tag', 'entityView'));

  app.bus.on('homeview/performConfirmedSearch', ({ term, searchMethod, referrer }) =>
    performConfirmedQuery(term, searchMethod, referrer),
  );

  function showCategoryList() {
    app.bus.send('layers/hide', { id: SEARCH_VIEW_BODY_ID });
    app.bus.send('layers/show', { id: SEARCH_VIEW_CATEGORY_LIST_ID });
  }

  function showResultsWidget() {
    showSearchBody();
  }

  function showSearchBody() {
    app.bus.send('layers/hide', { id: SEARCH_VIEW_CATEGORY_LIST_ID });
    app.bus.send('layers/show', { id: SEARCH_VIEW_BODY_ID });
  }

  app.bus.on('homeView/hideCategory', ({ category }) => {
    hiddenCategories.add(category);
    setPOICategoryState();
  });

  /**
   * When POI names change dynamically, update any currently-displayed search results
   * so the SidebarWrapper reflects the new name without requiring a new search.
   */
  app.bus.on('poi/updated', () => {
    const { pois, nearby, locations } = searchResultsWidgetState.getState();

    if ([pois, nearby, locations].every(arr => Array.isArray(arr) && arr.length === 0)) {
      return;
    }

    searchResultsWidgetState.update({
      pois: pois.length ? [...pois] : pois,
      nearby: nearby.length ? [...nearby] : nearby,
      locations: locations.length ? [...locations] : locations,
    });
  });

  /**
   * When the typeahead index changes (due to POI name updates), re-run the active
   * typeahead search so the SidebarWrapper reflects the new results immediately.
   */
  app.bus.on('search/typeaheadChanged', async () => {
    const { isSearchConfirmed, lastTypeaheadTerm } = searchResultsWidgetState.getState();
    if (isSearchConfirmed || !lastTypeaheadTerm) {
      return;
    }
    await performSearchTypeahead(lastTypeaheadTerm, MAX_UI_RESULTS_LENGTH, false, 'typeahead', 'typeaheadUpdate');
  });

  // todo remove
  // Reset homeview confirmed search state
  app.bus.on('map/cleanMap', () => searchResultsWidgetState.update({ isSearchConfirmed: false }));

  app.bus.on('bluedot/locationUpdate', ({ hasLocation }) => {
    lastHasLocation = hasLocation;
    setPOICategoryState();
  });

  const runTest = async (initialState, testRoutine) => {
    searchResultsWidgetState.update({ ...initialState });
    await testRoutine();
    return searchResultsWidgetState.getState();
  };

  return {
    init,
    widgetState: () => searchResultsWidgetState,
    runTest,
    internal: {
      toggleViewHandler,
    },
  };
}

export { create };
